// Reconcile UI for a recipe's pending BOM lines (lines whose imported
// `material_name` did not match any existing material). Each pending line
// gets its own row with three branches:
//
//   - Pick existing  -> match the pending line to a chosen material
//   - Create new     -> create a brand-new material then match
//   - Discard        -> drop the pending line entirely
//
// On any successful reconcile the hook fires `onChanged` so the parent
// reloads the recipe detail (matched lines flip from pending into the
// live BOM) and the recipes list (the pending badge updates).

import { useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import type { MfgMaterial, MfgRecipePendingLine } from "@/lib/manufacturing/types";
import { MaterialSearchableSelect } from "./MaterialSearchableSelect";
import { useManufacturingPendingLines } from "./useManufacturingPendingLines";
import { adminDangerGhostBtnClass, ghostBtnClass, mfgInlineInputClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

type EditMode = "idle" | "pick" | "create";

export function ManufacturingModelsPendingLinesEditor({
  recipeId,
  materials,
  onChanged,
  onError,
}: {
  recipeId: string | null;
  materials: ReadonlyArray<MfgMaterial>;
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const flow = useManufacturingPendingLines({ recipeId, onChanged });

  if (!recipeId) return null;
  if (flow.rows.length === 0 && !flow.loading) return null;

  return (
    <SectionCard
      title={`Pending lines (${flow.rows.length})`}
      description="These imported BOM lines didn't match an existing material. Reconcile each one to finish the recipe."
    >
      {flow.loading ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">Loading pending lines…</div>
      ) : null}
      {flow.error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200">
          {flow.error}
        </div>
      ) : null}
      <div className="space-y-2">
        {flow.rows.map((row) => (
          <PendingLineRow
            key={row.id}
            row={row}
            materials={materials}
            busy={flow.busyId === row.id}
            onMatch={async (materialId) => {
              const r = await flow.matchExisting(row.id, materialId);
              if (!r.ok) onError(r.error);
            }}
            onCreateAndMatch={async (input) => {
              const r = await flow.createAndMatch(row.id, input);
              if (!r.ok) onError(r.error);
            }}
            onDiscard={async () => {
              const r = await flow.discard(row.id);
              if (!r.ok) onError(r.error);
            }}
          />
        ))}
      </div>
    </SectionCard>
  );
}

function PendingLineRow({
  row,
  materials,
  busy,
  onMatch,
  onCreateAndMatch,
  onDiscard,
}: {
  row: MfgRecipePendingLine;
  materials: ReadonlyArray<MfgMaterial>;
  busy: boolean;
  onMatch: (materialId: string) => Promise<void>;
  onCreateAndMatch: (input: { name: string; category: string; unit: string }) => Promise<void>;
  onDiscard: () => Promise<void>;
}) {
  const [mode, setMode] = useState<EditMode>("idle");
  const [newName, setNewName] = useState(row.material_name);
  const [newCategory, setNewCategory] = useState("MISC");
  const [newUnit, setNewUnit] = useState("each");

  return (
    <div className="rounded-lg border border-orange-200 bg-orange-50/40 p-3 text-sm dark:border-orange-800 dark:bg-orange-900/15">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-medium text-gray-900 dark:text-zinc-100">
          {row.material_name}
        </span>
        <span className="text-xs text-gray-600 dark:text-zinc-300">
          qty {row.quantity} · {row.role.replace("_", " ")}
          {row.slot_label ? ` · ${row.slot_label}` : ""}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          className={ghostBtnClass}
          onClick={() => setMode(mode === "pick" ? "idle" : "pick")}
          disabled={busy}
        >
          {mode === "pick" ? "Cancel pick" : "Pick existing"}
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          onClick={() => setMode(mode === "create" ? "idle" : "create")}
          disabled={busy}
        >
          {mode === "create" ? "Cancel create" : "Create new material"}
        </button>
        <button
          type="button"
          className={adminDangerGhostBtnClass}
          onClick={() => void onDiscard()}
          disabled={busy}
        >
          {busy ? "Working…" : "Discard"}
        </button>
      </div>

      {mode === "pick" ? (
        <div className="mt-2 space-y-1">
          <MaterialSearchableSelect
            materials={materials}
            initialQuery={row.material_name}
            disabled={busy}
            autoFocus
            onSelect={(m) => void onMatch(m.id)}
          />
          {busy ? (
            <div className="text-[11px] text-gray-500 dark:text-zinc-400">
              Matching…
            </div>
          ) : null}
        </div>
      ) : null}

      {mode === "create" ? (
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <label className="text-xs text-gray-600 dark:text-zinc-300">
            Name
            <input
              className={`${mfgInlineInputClass} mt-1 w-full`}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </label>
          <label className="text-xs text-gray-600 dark:text-zinc-300">
            Category
            <input
              className={`${mfgInlineInputClass} mt-1 w-full`}
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value.toUpperCase())}
              placeholder="e.g. FLOOR"
            />
          </label>
          <label className="text-xs text-gray-600 dark:text-zinc-300">
            Unit
            <input
              className={`${mfgInlineInputClass} mt-1 w-full`}
              value={newUnit}
              onChange={(e) => setNewUnit(e.target.value)}
              placeholder="each / sheet / lf"
            />
          </label>
          <div className="sm:col-span-3">
            <button
              type="button"
              className={mfgPrimaryBtnClass}
              disabled={!newName.trim() || !newCategory.trim() || !newUnit.trim() || busy}
              onClick={() =>
                void onCreateAndMatch({
                  name: newName.trim(),
                  category: newCategory.trim(),
                  unit: newUnit.trim(),
                })
              }
            >
              {busy ? "Creating…" : "Create & match"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
